import { Link } from "react-router-dom";
import { usePerspective } from "../lib/perspective";
import {
  useKundencockpit, useKpiLatest, useCockpitTrust,
  kpiAmpel, type Ampel, type KpiLatest, type GateRow, type GatePhase,
} from "../lib/kundencockpit";
import "../styles.w3.css";
import "../styles.w3b.css";

/**
 * Kundencockpit - verdichtete, beamer-taugliche Management-/Kundensicht (CompanyBrain Welle 3b).
 * Die 1&1-Gesamttransformation auf EINEN Blick: Programm-Ampel, KPI-Ampeln (Ist/Ziel/Erreichung),
 * Phasen-Gate-Matrix (Ressort x Phase), Teilprojekte, letztes KI-Briefing, offene Entscheidungen,
 * naechste Schritte - alles aus echten Daten (OE-Board/Gold/Runs/Decision-Log/Proaktive KI).
 * Rollenbewusst: Kundensicht = verdichtet (keine internen Steuerungsdetails). Deep-Links dezent.
 */

function fmtVal(v: number | null | undefined, unit?: string | null): string {
  if (v == null) return "—";
  const s = Number.isInteger(v) ? String(v) : v.toFixed(1);
  return unit ? `${s} ${unit}` : s;
}
function fmtDate(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function AmpelDot({ a, size = "md" }: { a: Ampel; size?: "sm" | "md" | "lg" }) {
  return <span className={`kc-dot kc-${a} kc-dot-${size}`} aria-hidden />;
}

function kpiName(k: KpiLatest): string {
  return k.display_name || k.name || k.metric_key || "KPI";
}

function KpiCard({ k }: { k: KpiLatest }) {
  const a = kpiAmpel(k);
  const bar = k.attainment_pct == null ? 0 : Math.max(0, Math.min(100, k.attainment_pct));
  return (
    <div className={`kc-kpi kc-border-${a}`}>
      <div className="kc-kpi-head">
        <AmpelDot a={a} />
        <span className="kc-kpi-name">{kpiName(k)}</span>
      </div>
      <div className="kc-kpi-val">{fmtVal(k.value, k.unit)}</div>
      <div className="kc-kpi-meta">
        <span>Ziel {fmtVal(k.target_value, k.unit)}</span>
        <strong>{k.attainment_pct != null ? `${k.attainment_pct.toFixed(0)} %` : "—"}{k.target_reached ? " ✓" : ""}</strong>
      </div>
      <div className="kc-kpi-bar"><span style={{ width: `${bar}%` }} className={`kc-fill kc-${a}`} /></div>
    </div>
  );
}

function GateMatrix({ phases, matrix, isBerater }: { phases: GatePhase[]; matrix: GateRow[]; isBerater: boolean }) {
  if (!phases.length || !matrix.length) {
    return <p className="muted small">Gate-Matrix derzeit nicht lesbar.</p>;
  }
  return (
    <div className="kc-matrix-wrap">
      <table className="kc-matrix">
        <thead>
          <tr>
            <th className="kc-mx-corner">Ressort</th>
            {phases.map((p) => (
              <th key={p.phase} title={p.titel}>{p.nr != null ? p.nr : ""} {p.titel.replace(/^\d+\s*/, "").split(" ").slice(0, 2).join(" ")}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.map((row) => (
            <tr key={row.ressort}>
              <th className="kc-mx-ressort" title={row.label}>{row.label}</th>
              {row.cells.map((c) => (
                <td key={c.phase} className={`kc-cell kc-bg-${c.ampel}`}
                    title={`${row.label} · ${c.status ?? "—"}${isBerater && c.kommentar ? " · " + c.kommentar : ""}`}>
                  <AmpelDot a={c.ampel} size="sm" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="kc-legend">
        <span><AmpelDot a="gruen" size="sm" /> im Plan</span>
        <span><AmpelDot a="gelb" size="sm" /> nicht begonnen</span>
        <span><AmpelDot a="rot" size="sm" /> in Verzug</span>
        <span><AmpelDot a="grau" size="sm" /> offen</span>
      </div>
    </div>
  );
}

export default function KundencockpitPage() {
  const { perspective, isBerater } = usePerspective();
  const q = useKundencockpit(perspective);
  const kpis = useKpiLatest();
  const trust = useCockpitTrust();

  const data = q.data;
  const program = data?.program;
  const gate = data?.phase_gate;
  const kpiList = (kpis.data ?? []).filter((k) => k.value != null || k.target_value != null);

  return (
    <section className="kc-page" aria-label="Kundencockpit">
      <header className="kc-hero">
        <div className="kc-hero-copy">
          <p className="hero-eyebrow">CompanyBrain · {perspective === "berater" ? "Beratersicht (Impulse)" : "Managementsicht (1&1)"}</p>
          <h1>1&amp;1 Transformation — Cockpit auf einen Blick</h1>
          <p className="muted">
            {data?.tenant_display ?? "1&1"} · Datenstand {fmtDate(data?.generated_at)}
            {trust.data?.trust_gate && (
              <> · Datenvertrauen {trust.data.trust_gate.trusted_source_count ?? 0}/{trust.data.trust_gate.source_count ?? 0} Quellen</>
            )}
          </p>
        </div>
        {program && (
          <div className={`kc-program kc-border-${program.ampel}`} role="status" aria-label={`Programm-Status: ${program.label}`}>
            <AmpelDot a={program.ampel} size="lg" />
            <div>
              <span className="kc-program-k">Programm-Status gesamt</span>
              <strong>{program.label}</strong>
              <span className="muted small">{program.reason}</span>
            </div>
          </div>
        )}
      </header>

      {q.isLoading && <p className="muted">Cockpit wird geladen …</p>}
      {q.isError && <p className="w3-warn small">Cockpit-Daten derzeit nicht abrufbar. Bitte spaeter erneut versuchen.</p>}

      {/* KPI-Ampeln */}
      <div className="kc-block">
        <div className="kc-block-head"><h2>Kern-Kennzahlen</h2><span className="muted small">Ist · Ziel · Zielerreichung</span></div>
        {kpiList.length === 0 ? (
          <p className="muted small">Noch keine Kennzahlenwerte vorhanden.</p>
        ) : (
          <div className="kc-kpi-grid">{kpiList.map((k) => <KpiCard key={k.metric_key ?? kpiName(k)} k={k} />)}</div>
        )}
      </div>

      {/* Phasen-Gate-Matrix */}
      <div className="kc-block">
        <div className="kc-block-head">
          <h2>Phasen-Gate-Matrix</h2>
          <span className="muted small">
            {gate?.summary?.cells_total ? `${gate.summary.in_line ?? 0} von ${gate.summary.cells_total} Feldern im Plan (${gate.summary.progress_pct ?? 0} %)` : "Ressort × Phase"}
          </span>
        </div>
        {gate && gate.available ? (
          <GateMatrix phases={gate.phases} matrix={gate.matrix} isBerater={isBerater} />
        ) : (
          <p className="muted small">{gate?.reason ?? "OE-Board derzeit nicht lesbar."}</p>
        )}
      </div>

      <div className="kc-two">
        {/* Teilprojekte */}
        <div className="kc-block">
          <div className="kc-block-head"><h2>Teilprojekte je Ressort</h2></div>
          {gate && gate.teilprojekte.length > 0 ? (
            <ul className="kc-tp-list">
              {gate.teilprojekte.map((t) => (
                <li key={t.ressort}>
                  <div className="kc-tp-head"><strong>{t.label}</strong><span className="w3-pill kind">{t.count} TP</span></div>
                  {isBerater && t.tps.length > 0 && (
                    <ul className="kc-tp-sub">{t.tps.slice(0, 4).map((tp, i) => <li key={i} className="muted small">{tp}</li>)}</ul>
                  )}
                </li>
              ))}
            </ul>
          ) : <p className="muted small">Keine Teilprojekt-Daten lesbar.</p>}
        </div>

        {/* Letztes KI-Briefing */}
        <div className="kc-block">
          <div className="kc-block-head"><h2>Letztes KI-Steuerungsbriefing</h2></div>
          {(() => {
            const lb = data?.last_briefing;
            if (lb && lb.available) {
              return (
                <div className="kc-brief">
                  <div className="kc-brief-head"><strong>{lb.title}</strong>
                    <span className="muted small">{fmtDate(lb.created_at)}</span></div>
                  {lb.excerpt && <p className="muted">{lb.excerpt}…</p>}
                  <Link className="w3-link-chip" to={lb.route}>Briefing oeffnen →</Link>
                </div>
              );
            }
            return (
              <div className="kc-brief empty">
                <p className="muted small">{lb && !lb.available ? lb.reason : "Noch kein Briefing."}</p>
                <Link className="w3-link-chip" to={lb && !lb.available ? lb.cta_route : "/app/briefing-agent"}>
                  Briefing erzeugen →
                </Link>
              </div>
            );
          })()}
        </div>
      </div>

      <div className="kc-two">
        {/* Offene Entscheidungen */}
        <div className="kc-block">
          <div className="kc-block-head">
            <h2>Offene Entscheidungen</h2>
            <span className="w3-pill status-offen">{data?.decisions?.offen ?? 0} offen</span>
          </div>
          {data?.decisions?.items?.length ? (
            <ul className="kc-list">
              {data.decisions.items.map((d) => (
                <li key={d.id}><Link to={d.route}>{d.title}</Link></li>
              ))}
            </ul>
          ) : <p className="muted small">Keine offenen Entscheidungen.</p>}
          <Link className="w3-link-chip" to="/app/decisions">Zum Entscheidungs-Log →</Link>
        </div>

        {/* Naechste Schritte */}
        <div className="kc-block">
          <div className="kc-block-head"><h2>Naechste Schritte</h2>
            <span className="muted small">Proaktive KI</span></div>
          {data?.next_steps?.focus_today && <p className="kc-focus">{data.next_steps.focus_today}</p>}
          {data?.next_steps?.actions?.length ? (
            <ul className="kc-list">
              {data.next_steps.actions.map((a) => (
                <li key={a.id}>
                  <Link to={a.route}><strong>{a.title}</strong></Link>
                  {a.intervention && (
                    <Link className="w3-link-chip" to={a.intervention.route} title="Passende Intervention (verbatim aus Gold)">
                      → {a.intervention.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          ) : <p className="muted small">Aktuell keine dringenden Punkte aus den Daten.</p>}
        </div>
      </div>

      <footer className="kc-footer muted small">
        Verdichtete Sicht aus echten Daten (OE-Board, Gold-KPIs, Decision-Log, proaktive KI) · jede Aussage belegt · keine erfundenen Werte.
        {data?.deep_links && (
          <span className="kc-deeplinks">
            {data.deep_links.map((l) => <Link key={l.route} to={l.route} className="kc-deeplink">{l.label}</Link>)}
          </span>
        )}
      </footer>
    </section>
  );
}
